/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable react/no-unknown-property */
import React, { useMemo } from 'react';
import {
  IconButton,
  Tooltip,
  TextSubtitle,
  TextBody,
  TextCaption,
} from '@pypestream/design-system';
import { observer } from 'mobx-react-lite';

import { useProStudioCtxSelector } from '../../../xstate/app.xstate';
import { tableStore } from '../../../stores/table-store';
import { CsvUploader } from '../csv-uploader';
import { CsvDownloader } from '../csv-downloader';

import { autoRenumberToggleTitle } from './utils/auto-renumber-toggle-label';

import './controls-bar.scss';

// function isColumnId(
//   // eslint-disable-next-line @typescript-eslint/no-explicit-any
//   data: any
// ): data is keyof typeof NodeColumns {
//   // console.log('data', data, Object.keys(TableNode), data in Object.keys(TableNode));
//   return (data && data in NodeColumns) || false;
// }

// function canRenderColumnValue(
//   columnId: keyof typeof NodeColumns | undefined
// ): boolean {
//   if (columnId !== 'behaviors' && columnId !== 'parameterInput') return true;

//   return false;
// }

export const ControlsBar: React.FC<{
  openSupervisorPanel: () => void;
  supervisorPanelOpen: boolean;
  children?: React.ReactNode;
  viewOnly?: boolean;
  // PSTUDIO-3624: closes the overlay side panels (Supervisor / ResourceLib);
  // called when opening the Expanded Cell View panel.
  closeOverlaySidePanels?: () => void;
}> = observer(
  ({
    openSupervisorPanel,
    supervisorPanelOpen,
    children,
    viewOnly,
    closeOverlaySidePanels,
  }) => {
    const { draftVersionSolutionData } = useProStudioCtxSelector((ctx) => ({
      draftVersionSolutionData: ctx.draftVersionSolutionData,
    }));

    const isSupervisorConfigured = useMemo(
      () => draftVersionSolutionData?.llmCredential?.llmProviderId,
      [draftVersionSolutionData]
    );

    // PSTUDIO-2154: Google-Sheets-style tooltips showing the OS-appropriate
    // keyboard shortcut for the undo/redo toolbar buttons.
    const isMac = useMemo(
      () =>
        /Mac|iPhone|iPad|iPod/.test(
          navigator.platform || navigator.userAgent || ''
        ),
      []
    );
    const undoTooltip = isMac ? 'Undo (⌘Z)' : 'Undo (Ctrl+Z)';
    const redoTooltip = isMac ? 'Redo (⌘⇧Z)' : 'Redo (Ctrl+Y)';
    const autoRenumberShortcut = isMac ? '⌘⇧S' : 'Ctrl+Shift+S';
    const expandedShortcut = isMac ? '⇧↵' : 'Shift+Enter';

    return (
      <div
        test-id="ps-editor-table-controls-bar"
        className="c-editor-table-controls-bar"
      >
        {/**
      <div className="c-editor-table-controls-bar__info">
        <span>
          <>
            {tableStore.selectedCellColumnIndex() || '?'}:
            {tableStore.selectedCellRowIndex() || '?'}
          </>
        </span>
      </div>
      */}
        {/* PSTUDIO-3624: undo/redo sit at the far LEFT, before the search bar
            (Figma design) — a divider (the actions border) separates them from
            Filter Nodes. */}
        {!viewOnly && (
          <div className="c-editor-table-controls-bar__actions c-editor-table-controls-bar__history-actions">
            <Tooltip content={undoTooltip}>
              <IconButton
                test-id="ps-undo-btn"
                size="small"
                name="undo"
                disabled={!tableStore.canUndo}
                onClick={() => tableStore.undo()}
              />
            </Tooltip>
            <Tooltip content={redoTooltip}>
              <IconButton
                test-id="ps-redo-btn"
                size="small"
                name="redo"
                disabled={!tableStore.canRedo}
                onClick={() => tableStore.redo()}
              />
            </Tooltip>
          </div>
        )}
        {children && (
          <div className="c-editor-table-controls-bar__actions c-editor-table-controls-bar__row-actions">
            {children}
          </div>
        )}
        {/* PSTUDIO-3624: flexible spacer pushes the remaining action buttons to
            the far RIGHT (the old formula-bar textarea used to fill this space;
            after it moved to the panel the buttons must stay right-aligned,
            including when the Expanded Cell View panel narrows the toolbar). */}
        <div className="c-editor-table-controls-bar__spacer" />
        {!viewOnly && (
          <div className="c-editor-table-controls-bar__actions c-editor-table-controls-bar__row-actions">
            <Tooltip placement="bottom">
              <IconButton
                className="c-auto-renumber-toggle"
                test-id="ps-auto-renumber-toggle-btn"
                size="small"
                name="hash-down"
                selected={tableStore.autoRenumberOnPaste}
                onClick={() => tableStore.toggleAutoRenumberOnPaste()}
              />
              <div slot="content">
                <TextSubtitle size="small">
                  {autoRenumberToggleTitle(
                    tableStore.autoRenumberOnPaste,
                    autoRenumberShortcut
                  )}
                </TextSubtitle>
                <TextBody size="small">
                  {tableStore.autoRenumberOnPaste
                    ? 'Pasted nodes keep their original node numbers when possible. If a node number is already used elsewhere in the solution, the pasted node and any references to it in the paste are renumbered to match, so nothing breaks.'
                    : 'Pasted nodes keep their original node numbers exactly. If a number is already used elsewhere in the solution, the pasted node keeps that number too — the duplicate is flagged by validation, not renumbered.'}
                </TextBody>
              </div>
            </Tooltip>
          </div>
        )}
        {!viewOnly && (
          <div className="c-editor-table-controls-bar__actions c-editor-table-controls-bar__row-actions">
            <Tooltip placement="bottom">
              <IconButton
                className="c-keyboard-shortcuts-toggle"
                test-id="ps-keyboard-shortcuts-toggle-btn"
                size="small"
                name="keyboard"
                selected={tableStore.keyboardShortcutsOpen}
                onClick={() => {
                  // PSTUDIO-3623: opening the help panel closes the overlay
                  // side panels (Supervisor / ResourceLib); the store setter
                  // additionally closes Expanded Cell View (one panel at a time).
                  if (!tableStore.keyboardShortcutsOpen) {
                    closeOverlaySidePanels?.();
                  }
                  tableStore.toggleKeyboardShortcutsOpen();
                }}
              />
              <div slot="content">
                <TextCaption variant="primary">
                  {`Keyboard shortcuts (${isMac ? '⌘/' : 'Ctrl+/'})`}
                </TextCaption>
              </div>
            </Tooltip>
          </div>
        )}
        {!viewOnly && (
          <div className="c-editor-table-controls-bar__actions c-editor-table-controls-bar__row-actions">
            <Tooltip placement="bottom">
              <IconButton
                className="c-expanded-cell-view-toggle"
                test-id="ps-expanded-cell-view-toggle-btn"
                size="small"
                name="panel-right"
                selected={tableStore.expandedCellViewOpen}
                onClick={() => {
                  if (!tableStore.expandedCellViewOpen) {
                    closeOverlaySidePanels?.();
                  }
                  tableStore.toggleExpandedCellViewOpen();
                }}
              />
              <div slot="content">
                {/* Figma tooltip: "Other/Caption" (Gordita 400, 11px/16px) =
                    TextCaption. Main label is primary (white); the shortcut hint
                    is muted #D9D9D9 per the design. The DS Tooltip container
                    already matches (dark bg, 4px radius, ~32px tall). */}
                <TextCaption variant="primary">
                  Expanded cell view{' '}
                  <span className="c-expanded-cell-view-toggle__shortcut">
                    ({expandedShortcut})
                  </span>
                </TextCaption>
              </div>
            </Tooltip>
          </div>
        )}
        <div className="c-editor-table-controls-bar__actions c-editor-table-controls-bar__row-actions">
          {!viewOnly && (
            <Tooltip content="Upload CSV">
              <CsvUploader />
            </Tooltip>
          )}

          <Tooltip content="Download Solution">
            <CsvDownloader />
          </Tooltip>
        </div>
        {isSupervisorConfigured && !supervisorPanelOpen && (
          <div className="c-editor-table-controls-bar__actions c-editor-table-controls-bar__row-actions">
            <Tooltip content="Configure Agents">
              <IconButton
                test-id="ps-agents-panel-open-btn"
                size="small"
                name="stars"
                onClick={openSupervisorPanel}
              />
            </Tooltip>
          </div>
        )}
        {/**
      <div className="c-editor-table-controls-bar__actions c-editor-table-controls-bar__row-actions">
        <IconButton size="small" name="text-overflow" />
        <IconButton size="small" name="text-clip" />
        <IconButton size="small" name="text-wrap" />
      </div>
      */}
      </div>
    );
  }
);
